import Select, {
  type SelectProps,
  type SelectRef,
} from "@src/components/ui-kit/Select/Select";
import type { IOption } from "@src/components/ui-kit/Select/types";
import { action, makeObservable, observable } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import { createRef, useImperativeHandle } from "react";
import type { SetValueAction } from "react-select";

export type SelectManualProps = Omit<
  SelectProps,
  "onInputChange" | "onKeyDown"
> & {
  options: IOption[];
  allowCreateOnBlur?: boolean;
  onCreateOption?: (
    optionLabel: IOption["label"],
  ) => Promise<IOption | null | undefined>;
};
export const SelectManual = observer<SelectManualProps, SelectRef>(
  function SelectManual(props, forwardedRef) {
    const state = useLocalObservable(() => new SelectManualState());

    useImperativeHandle(forwardedRef, () => {
      return {
        focus() {
          state.selectRef.current?.focus();
        },
        onMenuClose() {
          state.selectRef.current?.onMenuClose();
        },
        setValue(value: unknown, action: SetValueAction, option?: unknown) {
          state.selectRef.current?.setValue(value, action, option);
        },
      };
    }, []);

    return (
      <Select
        ref={state.selectRef}
        hideSelectedOptions
        {...props}
        onBlur={() => {
          if (!props.allowCreateOnBlur) return;
          state.handleBlur(props.options, props.onCreateOption);
        }}
        onKeyDown={(e) => {
          state.handleKeyDown(e, props.options, props.onCreateOption);
        }}
        inputValue={state.inputValue}
        onInputChange={state.handleInputChange}
      />
    );
  },
  { forwardRef: true },
);

class SelectManualState {
  @observable.ref inputValue = "";
  selectRef = createRef<SelectRef>();

  constructor() {
    makeObservable(this);
  }

  @action.bound handleInputChange(value: string) {
    this.inputValue = value;
  }

  @action.bound async handleKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    currentOptions: IOption[],
    onCreateOption?: (
      optionLabel: IOption["label"],
    ) => Promise<IOption | null | undefined>,
  ) {
    if (event.key !== "Enter" || event.code !== "Enter") return;
    if (this.inputValue === "") return;

    if (
      currentOptions.some((option) => option.label.startsWith(this.inputValue))
    )
      return;

    const option = await onCreateOption?.(this.inputValue);
    if (!option) return;

    this.handleInputChange("");
    this.selectRef?.current?.focus();
  }

  @action.bound async handleBlur(
    currentOptions: IOption[],
    onCreateOption?: (
      optionLabel: IOption["label"],
    ) => Promise<IOption | null | undefined>,
  ) {
    if (this.inputValue === "") return;

    if (
      currentOptions.some((option) => option.label.startsWith(this.inputValue))
    )
      return;

    const option = await onCreateOption?.(this.inputValue);
    if (!option) return;

    this.handleInputChange("");
  }
}
